import { useState, useEffect } from 'react'
import AdminSidebar from '../../components/admin/AdminSidebar'
import { apiFetch } from '../../utils/api'
import './AdminProjectsPage.css'

const STATUS_CONFIG = {
  planning:    { label: 'Planning',    color: '#64b4ff', bg: 'rgba(100,180,255,0.1)',  border: 'rgba(100,180,255,0.3)'  },
  in_progress: { label: 'In Progress', color: '#ffc850', bg: 'rgba(255,200,80,0.1)',   border: 'rgba(255,200,80,0.3)'   },
  on_hold:     { label: 'On Hold',     color: '#FFB7CE', bg: 'rgba(255,183,206,0.1)',  border: 'rgba(255,183,206,0.3)'  },
  completed:   { label: 'Completed',   color: '#78dca0', bg: 'rgba(120,220,160,0.1)',  border: 'rgba(120,220,160,0.3)'  },
  cancelled:   { label: 'Cancelled',   color: 'rgba(255,255,255,0.3)', bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.1)' },
}

const ACTIVE_STATUSES = ['planning', 'in_progress', 'on_hold']

const EMPTY_FORM = {
  quote_id: null, client_name: '', client_email: '',
  client_company: '', services: '', title: '',
  description: '', price: '', status: 'planning',
  start_date: '', end_date: '', notes: '',
}

function fmt(n) {
  return `KSh ${Number(n || 0).toLocaleString('en-KE')}`
}

function StatusBadge({ status }) {
  const c = STATUS_CONFIG[status] || STATUS_CONFIG.planning
  return (
    <span className='ap-badge' style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.color }}>
      {c.label}
    </span>
  )
}

// ── Project Form Modal ────────────────────────────────────────────
function ProjectModal({ project, onClose, onSaved }) {
  const [form, setForm]   = useState(project ? { ...project, price: project.price || '' } : { ...EMPTY_FORM })
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState(null)
  const isEdit              = !!project?.id
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const save = async () => {
    if (!form.client_name || !form.title) { setError('Client name and project title are required.'); return }
    setSaving(true); setError(null)
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0 }
      const res  = await apiFetch(
        isEdit ? `/api/admin/projects/${project.id}` : '/api/admin/projects',
        { method: isEdit ? 'PUT' : 'POST', body: JSON.stringify(payload) }
      )
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || 'Failed to save.')
      onSaved(data)
    } catch(e) { setError(e.message) }
    finally { setSaving(false) }
  }

  return (
    <div className='ap-overlay' onClick={onClose}>
      <div className='ap-modal' onClick={e => e.stopPropagation()}>
        <div className='ap-modal-header'>
          <h3>{isEdit ? 'Edit Project' : 'Add Project'}</h3>
          <button className='ap-modal-close' onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div className='ap-modal-body'>
          {/* Project title */}
          <div className='ap-field'>
            <label>Project Title <span>*</span></label>
            <input value={form.title} onChange={e => set('title', e.target.value)} placeholder='e.g. Soko Fresh — E-commerce Platform' />
          </div>

          {/* Client row */}
          <div className='ap-row'>
            <div className='ap-field'>
              <label>Client Name <span>*</span></label>
              <input value={form.client_name} onChange={e => set('client_name', e.target.value)} placeholder='Jane Doe' />
            </div>
            <div className='ap-field'>
              <label>Company <span className='opt'>(optional)</span></label>
              <input value={form.client_company} onChange={e => set('client_company', e.target.value)} placeholder='Acme Inc.' />
            </div>
          </div>

          <div className='ap-row'>
            <div className='ap-field'>
              <label>Client Email <span className='opt'>(optional)</span></label>
              <input type='email' value={form.client_email} onChange={e => set('client_email', e.target.value)} placeholder='client@email.com' />
            </div>
            <div className='ap-field'>
              <label>Services</label>
              <input value={form.services} onChange={e => set('services', e.target.value)} placeholder='Software Development, UI/UX' />
            </div>
          </div>

          {/* Description */}
          <div className='ap-field'>
            <label>Description <span className='opt'>(optional)</span></label>
            <textarea rows={2} value={form.description} onChange={e => set('description', e.target.value)} placeholder='Brief project scope…' />
          </div>

          {/* Price + Status */}
          <div className='ap-row'>
            <div className='ap-field'>
              <label>Project Price (KSh) <span>*</span></label>
              <input type='number' value={form.price} onChange={e => set('price', e.target.value)} placeholder='150000' />
            </div>
            <div className='ap-field'>
              <label>Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)}>
                {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Dates */}
          <div className='ap-row'>
            <div className='ap-field'>
              <label>Start Date</label>
              <input type='date' value={form.start_date || ''} onChange={e => set('start_date', e.target.value)} />
            </div>
            <div className='ap-field'>
              <label>End / Delivery Date</label>
              <input type='date' value={form.end_date || ''} onChange={e => set('end_date', e.target.value)} />
            </div>
          </div>

          {/* Notes */}
          <div className='ap-field'>
            <label>Internal Notes <span className='opt'>(optional)</span></label>
            <textarea rows={2} value={form.notes || ''} onChange={e => set('notes', e.target.value)} placeholder='Any internal context, payment terms, etc.' />
          </div>

          {error && <p className='ap-error'>⚠ {error}</p>}
        </div>

        <div className='ap-modal-footer'>
          <button className='ap-btn-cancel' onClick={onClose}>Cancel</button>
          <button className='ap-btn-save' onClick={save} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Project'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Project card ──────────────────────────────────────────────────
function ProjectCard({ project, onEdit, onDelete, onStatusChange }) {
  const [changing, setChanging] = useState(false)

  const changeStatus = async (status) => {
    setChanging(true)
    try {
      const res  = await apiFetch(`/api/admin/projects/${project.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      })
      const data = await res.json()
      if (res.ok) onStatusChange(project.id, status, data)
    } finally { setChanging(false) }
  }

  const handleDelete = async () => {
    if (!window.confirm(`Delete project "${project.title}"? This cannot be undone.`)) return
    await apiFetch(`/api/admin/projects/${project.id}`, { method: 'DELETE' })
    onDelete(project.id)
  }

  return (
    <div className={`ap-card ap-card--${project.status}`}>
      <div className='ap-card-top'>
        <div className='ap-card-title-row'>
          <h3 className='ap-card-title'>{project.title}</h3>
          <StatusBadge status={project.status} />
        </div>
        <div className='ap-card-client'>
          <div className='ap-card-avatar'>
            {project.client_name?.split(' ').map(w=>w[0]).slice(0,2).join('').toUpperCase()}
          </div>
          <div>
            <span className='ap-card-name'>{project.client_name}</span>
            {project.client_company && <span className='ap-card-company'>{project.client_company}</span>}
          </div>
        </div>
      </div>

      {project.services && (
        <div className='ap-card-services'>
          {project.services.split(',').map(s => s.trim()).filter(Boolean).map(s => (
            <span key={s} className='ap-chip'>{s}</span>
          ))}
        </div>
      )}

      {project.description && <p className='ap-card-desc'>{project.description}</p>}

      <div className='ap-card-meta'>
        <span className='ap-card-price'>{fmt(project.price)}</span>
        <div className='ap-card-dates'>
          {project.start_date && <span>▶ {project.start_date}</span>}
          {project.end_date   && <span>⏹ {project.end_date}</span>}
        </div>
      </div>

      {/* Status switcher */}
      <div className='ap-card-statuses'>
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
          <button
            key={key}
            className={`ap-status-btn ${project.status === key ? 'ap-status-btn--active' : ''}`}
            style={project.status === key ? { background: cfg.bg, borderColor: cfg.border, color: cfg.color } : {}}
            onClick={() => changeStatus(key)}
            disabled={changing || project.status === key}
          >
            {cfg.label}
          </button>
        ))}
      </div>

      <div className='ap-card-actions'>
        <button className='ap-action-btn' onClick={() => onEdit(project)}>✏ Edit</button>
        <button className='ap-action-btn ap-action-btn--delete' onClick={handleDelete}>🗑 Delete</button>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────
export default function AdminProjectsPage() {
  const [projects, setProjects]   = useState([])
  const [loading, setLoading]     = useState(true)
  const [capacity, setCapacity]   = useState({ active_projects: 0, max_projects: 3 })
  const [modal, setModal]         = useState(false)
  const [editing, setEditing]     = useState(null)
  const [filter, setFilter]       = useState('all')
  const [income, setIncome]       = useState({ income_total: 0, income_this_month: 0 })

  const load = async () => {
    setLoading(true)
    try {
      const [projRes, incRes] = await Promise.all([
        apiFetch('/api/admin/projects'),
        apiFetch('/api/admin/projects/income'),
      ])
      const [proj, inc] = await Promise.all([projRes.json(), incRes.json()])
      setProjects(proj.projects || [])
      setCapacity({ active_projects: proj.active_projects, max_projects: proj.max_projects, at_capacity: proj.at_capacity })
      setIncome(inc)
    } finally { setLoading(false) }
  }

  useEffect(() => {
    load()
    // Check if redirected from Quotes page with prefill data
    const prefill = sessionStorage.getItem('prefill_project')
    if (prefill) {
      try {
        const data = JSON.parse(prefill)
        setEditing({ ...EMPTY_FORM, ...data })
        setModal(true)
      } catch { /* ignore */ }
      sessionStorage.removeItem('prefill_project')
    }
  }, [])

  const handleSaved = (data) => {
    setModal(false); setEditing(null)
    if (data.active_projects !== undefined) setCapacity(data)
    load()
  }

  const handleDelete = (id) => setProjects(p => p.filter(x => x.id !== id))

  const handleStatusChange = (id, status, capData) => {
    setProjects(p => p.map(x => x.id === id ? { ...x, status } : x))
    if (capData?.active_projects !== undefined) setCapacity(capData)
  }

  const counts = {
    all: projects.length,
    active: projects.filter(p => ACTIVE_STATUSES.includes(p.status)).length,
    completed: projects.filter(p => p.status === 'completed').length,
    on_hold: projects.filter(p => p.status === 'on_hold').length,
    cancelled: projects.filter(p => p.status === 'cancelled').length,
  }

  const filtered = filter === 'all' ? projects
    : filter === 'active' ? projects.filter(p => ACTIVE_STATUSES.includes(p.status))
    : projects.filter(p => p.status === filter)

  const atCapacity = capacity.active_projects >= capacity.max_projects

  return (
    <div className='ap-shell'>
      <AdminSidebar />

      <main className='ap-main'>
        {/* Topbar */}
        <div className='ap-topbar'>
          <div>
            <h1 className='ap-title'>Projects</h1>
            <p className='ap-sub'>{projects.length} total · {capacity.active_projects} active</p>
          </div>
          <div className='ap-topbar-right'>
            {/* Capacity indicator */}
            <div className={`ap-capacity ${atCapacity ? 'ap-capacity--full' : ''}`}>
              <div className='ap-capacity-bar'>
                {Array.from({ length: capacity.max_projects }).map((_, i) => (
                  <div
                    key={i}
                    className={`ap-capacity-slot ${i < capacity.active_projects ? 'ap-capacity-slot--filled' : ''}`}
                    style={i < capacity.active_projects ? { background: atCapacity ? '#ff8080' : '#FFB7CE' } : {}}
                  />
                ))}
              </div>
              <span>{capacity.active_projects}/{capacity.max_projects} slots</span>
            </div>
            <button className='ap-btn-add' onClick={() => { setEditing(null); setModal(true) }}>
              + New Project
            </button>
          </div>
        </div>

        {/* Income summary */}
        <div className='ap-income-row'>
          <div className='ap-income-card'>
            <span className='ap-income-label'>Total Earned</span>
            <span className='ap-income-value'>{fmt(income.income_total)}</span>
          </div>
          <div className='ap-income-card'>
            <span className='ap-income-label'>This Month</span>
            <span className='ap-income-value'>{fmt(income.income_this_month)}</span>
          </div>
          <div className='ap-income-card'>
            <span className='ap-income-label'>Completed Projects</span>
            <span className='ap-income-value'>{counts.completed}</span>
          </div>
          <div className='ap-income-card'>
            <span className='ap-income-label'>Active Projects</span>
            <span className='ap-income-value' style={{ color: atCapacity ? '#ff8080' : '#78dca0' }}>
              {capacity.active_projects}/{capacity.max_projects}
            </span>
          </div>
        </div>

        {/* Filter tabs */}
        <div className='ap-filter-bar'>
          {[
            ['all',       'All',        null],
            ['active',    'Active',     '#ffc850'],
            ['completed', 'Completed',  '#78dca0'],
            ['on_hold',   'On Hold',    '#FFB7CE'],
            ['cancelled', 'Cancelled',  'rgba(255,255,255,0.3)'],
          ].map(([key, label, color]) => (
            <button
              key={key}
              className={`ap-filter-btn ${filter === key ? 'ap-filter-btn--active' : ''}`}
              onClick={() => setFilter(key)}
            >
              {color && <span className='ap-filter-dot' style={{ background: color }}></span>}
              {label}
              <span className='ap-filter-count'>{counts[key] ?? projects.filter(p=>p.status===key).length}</span>
            </button>
          ))}
        </div>

        {loading && <div className='ap-state'><div className='ap-spinner'></div><p>Loading…</p></div>}

        {!loading && filtered.length === 0 && (
          <div className='ap-state'>
            <p>{filter !== 'all' ? `No ${filter} projects.` : 'No projects yet — add your first one!'}</p>
            {filter === 'all' && (
              <button className='ap-btn-add' onClick={() => setModal(true)}>+ New Project</button>
            )}
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className='ap-grid'>
            {filtered.map(p => (
              <ProjectCard
                key={p.id}
                project={p}
                onEdit={proj => { setEditing(proj); setModal(true) }}
                onDelete={handleDelete}
                onStatusChange={handleStatusChange}
              />
            ))}
          </div>
        )}
      </main>

      {modal && (
        <ProjectModal
          project={editing}
          onClose={() => { setModal(false); setEditing(null) }}
          onSaved={handleSaved}
        />
      )}
    </div>
  )
}